import TransactionLogger from '../actions/transactionLogger';
import {
  ENTITY_TYPE_PROJECT_INSURED,
  ENTITY_TYPE_DEFICIENCY,
  ENTITY_TYPE_CERTIFICATE,
  LOG_TYPE_CONFIRMED,
  LOG_TYPE_UNCONFIRMED,
  LOG_TYPE_WAIVED,
  LOG_TYPE_UNDO_WAIVED,
} from '../../../../lib/appConstants';

const DEFICIENCY_REVIEW_ACTIONS = {
  CONFIRM_ALL: 'CONFIRM_ALL',
  CONFIRM_SINGLE: 'CONFIRM_SINGLE',
  CONFIRM_COVERAGE: 'CONFIRM_COVERAGE',
  WAIVE_ALL: 'WAIVE_ALL',
  WAIVE_SINGLE: 'WAIVE_SINGLE',
  UNCONFIRM_COVERAGE: 'UNCONFIRM_COVERAGE',
  UNCONFIRM_SINGLE: 'UNCONFIRM_SINGLE',
  UNDO: 'UNDO',
};

export const getDeficiencyTransactionContext = (params = {}, options = {}) => {
  const loginProfile =
    params.loginProfile || params.profile || params.loggedInUser || null;

  const projectInsured = params.projectInsured || null;

  return {
    loginProfile,
    holderId: params.holderId || (projectInsured && projectInsured.HolderId) || null,
    action: options.action || params.action || null,
  };
};

export const buildDeficiencyTransactionPayload = (
  params = {},
  coverageDetails = [],
  projectInsuredId = null,
  extra = {}
) => {
  const certificatesList = params.certificatesList || [];
  const projectInsured = params.projectInsured || null;

  const certificateData =
    Array.isArray(certificatesList) && certificatesList.length > 0
      ? certificatesList[0]
      : {};

  return {
    projectId:
      certificateData.ProjectID || (projectInsured && projectInsured.ProjectId) || null,
    insuredId:
      certificateData.InsuredID || (projectInsured && projectInsured.InsuredId) || null,
    projectInsuredId: projectInsuredId || certificateData.ProjectInsuredID || null,
    documentId: certificateData.DocumentId || null,
    certificateId: certificateData.CertificateID || null,
    coverageDetails,
    ...extra,
  };
};

const normalizePerformedByDisplay = (loginProfile) => {
  if (!loginProfile) {
    return 'System';
  }

  const firstName = loginProfile.FirstName || '';
  const lastName = loginProfile.LastName || '';
  const displayName = `${firstName} ${lastName}`.trim();

  return displayName || loginProfile.Email || 'System';
};

const resolveTransactionTypeCode = (action) => {
  switch (action) {
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_ALL:
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_SINGLE:
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_COVERAGE:
      return LOG_TYPE_CONFIRMED;
    case DEFICIENCY_REVIEW_ACTIONS.WAIVE_ALL:
    case DEFICIENCY_REVIEW_ACTIONS.WAIVE_SINGLE:
      return LOG_TYPE_WAIVED;
    case DEFICIENCY_REVIEW_ACTIONS.UNCONFIRM_COVERAGE:
    case DEFICIENCY_REVIEW_ACTIONS.UNCONFIRM_SINGLE:
      return LOG_TYPE_UNCONFIRMED;
    case DEFICIENCY_REVIEW_ACTIONS.UNDO:
      return LOG_TYPE_UNDO_WAIVED;
    default:
      return null;
  }
};

const checkIsEntityTypePi = action =>
  action === DEFICIENCY_REVIEW_ACTIONS.CONFIRM_ALL ||
  action === DEFICIENCY_REVIEW_ACTIONS.WAIVE_ALL;


const resolveNewAttributeStatus = (action) => {
  switch (action) {
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_ALL:
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_SINGLE:
    case DEFICIENCY_REVIEW_ACTIONS.CONFIRM_COVERAGE:
      return 'Confirmed';
    case DEFICIENCY_REVIEW_ACTIONS.WAIVE_ALL:
    case DEFICIENCY_REVIEW_ACTIONS.WAIVE_SINGLE:
      return 'Waived';
    case DEFICIENCY_REVIEW_ACTIONS.UNCONFIRM_COVERAGE:
    case DEFICIENCY_REVIEW_ACTIONS.UNCONFIRM_SINGLE:
      return 'Unconfirmed';
    case DEFICIENCY_REVIEW_ACTIONS.UNDO:
      return 'Undo';
    default:
      return null;
  }
};

const getCoverageDetails = (payload = {}) => {
  if (!Array.isArray(payload.coverageDetails)) {
    return [];
  }

  return payload.coverageDetails;
};

const getFirstCoverageDetail = (payload = {}) => {
  const coverageDetails = getCoverageDetails(payload);
  return coverageDetails.length > 0 ? coverageDetails[0] : null;
};

const resolveCoverageType = (payload = {}) => {
  if (payload.coverageType) {
    return payload.coverageType;
  }

  const firstDetail = getFirstCoverageDetail(payload);
  return firstDetail ? firstDetail.CoverageType || firstDetail.coverageType || null : null;
};

const mapCoverageDetails = (coverageDetails, action) => {
  const newAttributeStatus = resolveNewAttributeStatus(action);

  return coverageDetails.map((detail) => {
    return {
      coverageType: detail.CoverageType || detail.coverageType || null,
      attributeName: detail.AttributeName || detail.attribute || null,
      deficiencyText: detail.DeficiencyText || detail.deficiencyText || null,
      currentAttributeStatus:
        detail.AttributeStatus || detail.CoverageStatus || null,
      currentAttributeStatusId: detail.CoverageAttributeStatusID || detail.CoverageStatusID || null,
      newAttributeStatus,
      projectInsuredDeficiencyId:
        detail.ProjectInsuredDeficiencyID || detail.projectInsuredDeficiencyId || null,
      deficiencyStatus: detail.DeficiencyStatusName || detail.deficiencyStatus || null,
      deficiencyStatusId: detail.DeficiencyStatusID || detail.deficiencyStatusId || null,
      documentStatusID: detail.DocumentStatusID || detail.documentStatusId || null,
      ruleGroupId: detail.RuleGroupID || detail.ruleGroupId || null,
      ruleId: detail.RuleID || detail.ruleId || null,
    };
  });
};

const isSingleDeficiencyAction = (action) => {
  return (
    action === DEFICIENCY_REVIEW_ACTIONS.CONFIRM_SINGLE ||
    action === DEFICIENCY_REVIEW_ACTIONS.WAIVE_SINGLE ||
    action === DEFICIENCY_REVIEW_ACTIONS.UNCONFIRM_SINGLE
  );
};

export const mapDeficiencyReviewPayload = async (payload, context) => {
  try {
    const {
      loginProfile,
      holderId,
      action,
    } = context;

    const transactionTypeCode = resolveTransactionTypeCode(action);

    if (!transactionTypeCode) {
      throw new Error(`Unknown deficiency review action: ${action}`);
    }

    const isEntityTypePi = checkIsEntityTypePi(action);
    const coverageDetails = getCoverageDetails(payload);
    const firstDetail = getFirstCoverageDetail(payload);
    const resolvedProjectInsuredId = payload.projectInsuredId || payload.ProjectInsuredID || null;
    const resolvedEntityId = payload.entityId
      || firstDetail.projectInsuredDeficiencyId
      || firstDetail.ProjectInsuredDeficiencyID
      || null;

    const performedByUserId = loginProfile && loginProfile.Id ? loginProfile.Id : null;
    
    return {
      performedByUserId,
      performedByDisplay: normalizePerformedByDisplay(loginProfile),
      holderId: holderId || null,
      projectId: payload.projectId || payload.ProjectID || null,
      insuredId: payload.insuredId || payload.InsuredID || null,
      projectInsuredId: resolvedProjectInsuredId,
      entityId: isEntityTypePi ? String(resolvedProjectInsuredId) : String(resolvedEntityId),
      transactionTypeCode,
      entityTypeCode: isEntityTypePi ? ENTITY_TYPE_PROJECT_INSURED : ENTITY_TYPE_DEFICIENCY,
      documentId: payload.documentId || payload.DocumentId || null,
      certificateId: payload.certificateId || payload.CertificateID || null,
      idempotencyKey: TransactionLogger.generateIdempotencyKey(),
      metadata: {
        action,
        actionSource: 'certificate_review_screen',
        timestamp: new Date().toISOString(),
        coverageType: resolveCoverageType(payload),
        coverageDetails: mapCoverageDetails(coverageDetails, action),
      },
    };
  } catch (error) {
    console.error('Error mapping deficiency review payload:', error);
    throw error;
  }
};

export const processDeficiencyReviewTransaction = async (payload, context = {}, options = {}) => {
  try {
    const resolvedContext = getDeficiencyTransactionContext(context, options);
    const transactionData = await mapDeficiencyReviewPayload(payload, resolvedContext);
    return await TransactionLogger.logTransaction(transactionData);
  } catch (error) {
    console.error('Deficiency review transaction logging failed:', error);
    return false;
  }
};

export {
  DEFICIENCY_REVIEW_ACTIONS,
};

export default {
  mapDeficiencyReviewPayload,
  processDeficiencyReviewTransaction,
  getDeficiencyTransactionContext,
  buildDeficiencyTransactionPayload,
  DEFICIENCY_REVIEW_ACTIONS,
};
